<script setup>
import { computed, ref } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { storeToRefs } from 'pinia'
import { useRestaurantsStore, useUsersStore } from '@/stores'

const props = defineProps({
  restaurantId: {
    type: String,
    default: null
  },

  // Allow passing friends data directly (e.g. from friends-favorites endpoint)
  friends: {
    type: Array,
    default: null
  },

  // The line's label. RM AI's Discovery cards say "Favorited by you/your network" (the
  // product agent, 10/20); every other surface keeps "Favorited by".
  label: {
    type: String,
    default: 'Favorited by'
  }
})

const emit = defineEmits(['click'])

const restaurantsStore = useRestaurantsStore()
const { friendsFavoritedByMap } = storeToRefs(restaurantsStore)

const usersStore = useUsersStore()
const { myUser } = storeToRefs(usersStore)

const isLoggedIn = computed(() => myUser.value && myUser.value.userLevel !== 'dummy')

const friendsWhoFavorited = computed(() => {
  // Use directly passed friends if available
  if (props.friends) return props.friends

  // Otherwise look up from the store map
  if (!props.restaurantId) return []
  
  return friendsFavoritedByMap.value[props.restaurantId] || []
})

// Show up to 3 avatars; "+N more" covers everyone beyond those shown.
const shownCount = computed(() => Math.min(3, friendsWhoFavorited.value.length))
const remainingCount = computed(() => Math.max(0, friendsWhoFavorited.value.length - shownCount.value))

// A favoriter whose photo is missing or fails to load falls back to their
// initials in a circle — the row must never render an empty avatar
const failedImageIds = ref(new Set())
const onImageError = id => {
  failedImageIds.value = new Set([...failedImageIds.value, id])
}
const showImage = friend => !!friend.profileImageId && !failedImageIds.value.has(friend.profileImageId)
</script>

<template>
  <div
    v-if="isLoggedIn && friendsWhoFavorited.length > 0"
    class="friends-liked-row"
    @click.stop="emit('click')"
  >
    <span class="friends-liked-text">{{ label }}</span>
    <div class="friends-avatars-stack">
      <VAvatar
        v-for="(friend, i) in friendsWhoFavorited.slice(0, 3)"
        :key="i"
        size="20"
        :class="showImage(friend) ? 'friends-avatar' : 'friends-avatar friends-avatar-initials'"
        :style="{ zIndex: 3 - i, marginLeft: i > 0 ? '-8px' : '0', position: 'relative' }"
      >
        <VImg
          v-if="showImage(friend)"
          :src="mediaUrl(friend.profileImageId, 'thumb')"
          @error="onImageError(friend.profileImageId)"
        />
        <span
          v-else
          class="friends-avatar-fallback-text"
        >{{ (friend.firstName || '?').charAt(0).toUpperCase() }}</span>
      </VAvatar>
    </div>
    <span
      v-if="remainingCount > 0"
      class="friends-liked-text"
    >+{{ remainingCount }} more</span>
  </div>
</template>

<style lang="scss" scoped>
.friends-liked-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-top: 4px;
  cursor: pointer;
  min-width: 0;

  &:hover .friends-liked-text {
    color: var(--ds-brand-primary-dark);
  }
}

.friends-avatars-stack {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.friends-avatar {
  border: 1.5px solid white;
  flex-shrink: 0;
}

.friends-avatar-initials {
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
}

.friends-avatar-fallback-text {
  font-size: 0.5rem;
  font-weight: 500;
}

.friends-liked-text {
  font-family: var(--ds-font-body);
  font-size: 13px; // comfortable, per the master type scale
  color: var(--ds-text-body); // readable ink, not washed-out gray
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  transition: color 0.15s ease;
}
</style>
